/**
 * @file ReasonDialog.tsx
 * @description Окно кабинета «действие с обязательной причиной» (phase19.31,
 * зона «Заказы»; заменяет `window.prompt` в отмене заказа, возврате платежа и
 * отказе по возврату).
 *
 * Системное окно браузера не похоже на кабинет, на телефоне неудобно и не
 * объясняет последствия. Здесь — окно кабинета (на телефоне на весь экран):
 * что произойдёт, поле причины (без неё кнопка неактивна) и подтверждение.
 * Опасное действие — красной кнопкой (`danger`).
 */

import { useId, useState } from "react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

/** Тексты окна — задаёт место вызова. */
export interface ReasonDialogTexts {
  title: string;
  /** Что произойдёт после подтверждения. */
  description: string;
  label: string;
  placeholder?: string;
  confirm: string;
  cancel: string;
}

interface Props {
  open: boolean;
  texts: ReasonDialogTexts;
  busy: boolean;
  /** Опасное действие — красная кнопка подтверждения. */
  danger?: boolean;
  onClose: () => void;
  /** Подтвердить с причиной; `true` — получилось, окно закрывается. */
  onConfirm: (reason: string) => Promise<boolean>;
}

export function ReasonDialog({ open, texts, busy, danger = false, onClose, onConfirm }: Props) {
  const fieldId = useId();
  const [reason, setReason] = useState("");
  const ready = reason.trim().length > 0;

  function close() {
    setReason("");
    onClose();
  }

  async function confirm() {
    if (!ready) return;
    if (await onConfirm(reason.trim())) close();
  }

  return (
    <AlertDialog open={open} onOpenChange={(value) => (!value ? close() : null)}>
      <AlertDialogContent fullScreen className="max-w-md gap-3">
        <AlertDialogHeader>
          <AlertDialogTitle>{texts.title}</AlertDialogTitle>
          <AlertDialogDescription>{texts.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={fieldId}>{texts.label}</Label>
          <Textarea
            id={fieldId}
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={texts.placeholder}
            aria-required
          />
        </div>
        <AlertDialogFooter>
          <Button variant="ghost" onClick={close} disabled={busy}>
            {texts.cancel}
          </Button>
          <Button
            variant={danger ? "destructive-solid" : "brand"}
            onClick={() => void confirm()}
            disabled={!ready || busy}
            loading={busy}
          >
            {texts.confirm}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
